<template>
  <div
    v-bind="$attrs"
    ref="triggerRef"
    class="tooltip-trigger"
    @mouseenter="onMouseEnter"
    @mouseleave="onMouseLeave"
  >
    <slot :active="visible" @click="visible = true" />
  </div>

  <Teleport :to="popupContainer" v-if="enabled">
    <div
      v-if="visible"
      ref="tooltipRef"
      class="tooltip"
      :class="[`tooltip--${effectivePlacement}`, `tooltip--${theme}`, contentClass, maxWidth]"
      :style="{ 'max-width': maxWidth, ...tooltipStyle }"
      @mouseenter="onTooltipMouseEnter"
      @mouseleave="onTooltipMouseLeave"
    >
      <div class="tooltip__content">
        <slot name="content" :content="content">
          {{ content }}
        </slot>
      </div>
      <TipArrow
        class="tooltip__arrow"
        :class="[`tooltip__arrow--${effectivePlacement}`, `tooltip__arrow--${theme}`]"
      >
      </TipArrow>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { useThrottleFn } from '@vueuse/core';
import { computed, nextTick, onMounted, onUnmounted, ref, useSlots, watch } from 'vue';
import { elementObserverManager } from '../../libs/element-observer-manager';
import { getWindow } from '../../utils/ssr';
import TipArrow from './tip-arrow.svg?component';
import type { TooltipPlacement, TooltipProps } from './types';

/** placement 与相反方向的映射 */
const PLACEMENT_OPPOSITE: Record<TooltipPlacement, TooltipPlacement> = {
  top: 'bottom',
  bottom: 'top',
  topLeft: 'bottomLeft',
  topRight: 'bottomRight',
  bottomLeft: 'topLeft',
  bottomRight: 'topRight',
  left: 'right',
  right: 'left',
  leftTop: 'rightTop',
  leftBottom: 'rightBottom',
  rightTop: 'leftTop',
  rightBottom: 'leftBottom',
};

const props = withDefaults(defineProps<TooltipProps>(), {
  content: '',
  placement: 'top',
  trigger: 'hover',
  disabled: false,
  contentClass: '',
  offset: 8, // 保证箭头到元素的距离接近 4 像素
  crossOffset: 0, // 交叉轴偏移，默认为 0
  enabled: true,
  theme: 'dark',
  maxWidth: '350px',
  getPopupContainer: undefined,
  autoAdjust: true,
});

const slots = useSlots();

// 计算 tooltip 挂载的容器
const popupContainer = computed(() => {
  if (props.getPopupContainer) {
    return props.getPopupContainer();
  }
  return 'body';
});

const triggerRef = ref<HTMLElement>();
const tooltipRef = ref<HTMLElement>();
const visible = ref(props.open);
/** 实际使用的 placement，autoAdjust 可能切换为相反方向 */
const effectivePlacement = ref<TooltipPlacement>(props.placement);

const tooltipStyle = ref({
  position: 'absolute' as const,
  top: '0px',
  left: '0px',
  zIndex: 9999,
  opacity: 0,
});

/**
 * 获取容器的偏移信息
 * 用于计算相对于自定义容器的定位
 */
const getContainerOffset = () => {
  const win = getWindow();
  if (!win) return { offsetX: 0, offsetY: 0 };

  const container = props.getPopupContainer?.();
  if (!container || container === document.body) {
    // 默认挂载到 body，使用页面滚动偏移
    return { offsetX: win.scrollX, offsetY: win.scrollY };
  }

  // 自定义容器：计算容器相对于视口的位置
  const containerRect = container.getBoundingClientRect();
  const containerStyle = win.getComputedStyle(container);

  // 获取容器的滚动偏移
  const containerScrollLeft = container.scrollLeft;
  const containerScrollTop = container.scrollTop;

  // 获取容器的 border 宽度（需要从定位中减去）
  const borderLeft = parseFloat(containerStyle.borderLeftWidth) || 0;
  const borderTop = parseFloat(containerStyle.borderTopWidth) || 0;

  // 计算偏移：容器内的定位需要减去容器的视口位置，加上容器的滚动偏移
  return {
    offsetX: -containerRect.left + containerScrollLeft - borderLeft,
    offsetY: -containerRect.top + containerScrollTop - borderTop,
  };
};

/**
 * 根据 placement 计算 tooltip 在视口中的 top/left（未加容器偏移）
 */
const calcPositionForPlacement = (
  placement: TooltipPlacement,
  triggerRect: DOMRect,
  tooltipRect: DOMRect,
  offset: number,
  crossOffset: number,
) => {
  let top = 0;
  let left = 0;
  switch (placement) {
    case 'top':
      top = triggerRect.top - tooltipRect.height - offset;
      left = triggerRect.left + (triggerRect.width - tooltipRect.width) / 2 + crossOffset;
      break;
    case 'topLeft':
      top = triggerRect.top - tooltipRect.height - offset;
      left = triggerRect.left - 3 + crossOffset;
      break;
    case 'topRight':
      top = triggerRect.top - tooltipRect.height - offset;
      left = triggerRect.right - tooltipRect.width + 3 + crossOffset;
      break;
    case 'bottom':
      top = triggerRect.bottom + offset;
      left = triggerRect.left + (triggerRect.width - tooltipRect.width) / 2 + crossOffset;
      break;
    case 'bottomLeft':
      top = triggerRect.bottom + offset;
      left = triggerRect.left - 3 + crossOffset;
      break;
    case 'bottomRight':
      top = triggerRect.bottom + offset;
      left = triggerRect.right - tooltipRect.width + 3 + crossOffset;
      break;
    case 'left':
      top = triggerRect.top + (triggerRect.height - tooltipRect.height) / 2 + crossOffset;
      left = triggerRect.left - tooltipRect.width - offset;
      break;
    case 'leftTop':
      top = triggerRect.top + crossOffset;
      left = triggerRect.left - tooltipRect.width - offset;
      break;
    case 'leftBottom':
      top = triggerRect.bottom - tooltipRect.height + crossOffset;
      left = triggerRect.left - tooltipRect.width - offset;
      break;
    case 'right':
      top = triggerRect.top + (triggerRect.height - tooltipRect.height) / 2 + crossOffset;
      left = triggerRect.right + offset;
      break;
    case 'rightTop':
      top = triggerRect.top + crossOffset;
      left = triggerRect.right + offset;
      break;
    case 'rightBottom':
      top = triggerRect.bottom - tooltipRect.height + crossOffset;
      left = triggerRect.right + offset;
      break;
  }
  return { top, left };
};

/** 判断 placement 方向在视口中是否空间不足 */
const isPlacementOverflow = (
  placement: TooltipPlacement,
  top: number,
  left: number,
  tooltipRect: DOMRect,
  viewportWidth: number,
  viewportHeight: number,
) => {
  const pad = 8;
  const verticalTop = ['top', 'topLeft', 'topRight'];
  const verticalBottom = ['bottom', 'bottomLeft', 'bottomRight'];
  const horizontalLeft = ['left', 'leftTop', 'leftBottom'];
  const horizontalRight = ['right', 'rightTop', 'rightBottom'];

  if (verticalTop.includes(placement)) return top < pad;
  if (verticalBottom.includes(placement)) return top + tooltipRect.height > viewportHeight - pad;
  if (horizontalLeft.includes(placement)) return left < pad;
  if (horizontalRight.includes(placement)) return left + tooltipRect.width > viewportWidth - pad;
  return false;
};

const updatePosition = async () => {
  if (!triggerRef.value || !tooltipRef.value || !visible.value) return;
  const win = getWindow();
  if (!win) return; // SSR 安全检查

  await nextTick();
  await nextTick(); // 双重 nextTick 确保 DOM 已完全渲染

  const triggerRect = triggerRef.value.getBoundingClientRect();
  const tooltipRect = tooltipRef.value.getBoundingClientRect();
  const { offset, crossOffset, placement } = props;

  const { offsetX, offsetY } = getContainerOffset();
  const viewportWidth = win.innerWidth;
  const viewportHeight = win.innerHeight;

  // 确定实际使用的 placement（autoAdjust 时可能切换为相反方向）
  let placementToUse = placement;
  if (props.autoAdjust) {
    const pos = calcPositionForPlacement(placement, triggerRect, tooltipRect, offset, crossOffset);
    if (
      isPlacementOverflow(placement, pos.top, pos.left, tooltipRect, viewportWidth, viewportHeight)
    ) {
      placementToUse = PLACEMENT_OPPOSITE[placement];
    }
  }
  effectivePlacement.value = placementToUse;

  let { top, left } = calcPositionForPlacement(
    placementToUse,
    triggerRect,
    tooltipRect,
    offset,
    crossOffset,
  );

  // 确保 tooltip 不会超出视窗
  if (left < 8) left = 8;
  if (left + tooltipRect.width > viewportWidth - 8) {
    left = viewportWidth - tooltipRect.width - 8;
  }
  if (top < 8) top = 8;
  if (top + tooltipRect.height > viewportHeight - 8) {
    top = viewportHeight - tooltipRect.height - 8;
  }

  // 应用容器偏移
  top += offsetY;
  left += offsetX;

  tooltipStyle.value = {
    position: 'absolute',
    top: `${top}px`,
    left: `${left}px`,
    zIndex: 9999,
    opacity: 1,
  };
};

const updatePositionThrottled = useThrottleFn(updatePosition, 100);

const show = () => {
  // 如果禁用，或者既没有 content prop 也没有 content slot，则不显示
  if (props.disabled || (!props.content && !slots.content)) return;
  visible.value = true;
  nextTick(() => {
    updatePosition();
  });
};

const hide = () => {
  visible.value = false;
  tooltipStyle.value.opacity = 0;
};

let hideTimer: ReturnType<typeof setTimeout> | null = null;

const onMouseEnter = () => {
  if (props.trigger === 'hover') {
    // 清除可能存在的隐藏定时器
    if (hideTimer) {
      clearTimeout(hideTimer);
      hideTimer = null;
    }
    show();
  }
};

const onMouseLeave = () => {
  if (props.trigger === 'hover') {
    // 延迟隐藏，给鼠标移动到 tooltip 内容区的时间
    hideTimer = setTimeout(() => {
      hide();
    }, 100);
  }
};

// 当鼠标进入 tooltip 内容区时，保持显示
const onTooltipMouseEnter = () => {
  if (props.trigger === 'hover') {
    // 清除可能存在的隐藏定时器
    if (hideTimer) {
      clearTimeout(hideTimer);
      hideTimer = null;
    }
    // 确保 tooltip 保持显示
    if (!visible.value) {
      show();
    }
  }
};

// 当鼠标离开 tooltip 内容区时，隐藏 tooltip
const onTooltipMouseLeave = () => {
  if (props.trigger === 'hover') {
    hide();
  }
};

const handleScroll = () => {
  if (visible.value) {
    updatePositionThrottled();
  }
};

// 检查触发元素是否在视口内
const checkTriggerInViewport = () => {
  if (!triggerRef.value) return true;
  const win = getWindow();
  if (!win) return true; // SSR 安全检查
  const rect = triggerRef.value.getBoundingClientRect();
  const isInViewport =
    rect.bottom > 0 && rect.right > 0 && rect.top < win.innerHeight && rect.left < win.innerWidth;

  if (!isInViewport && visible.value) {
    hide();
  }
  return isInViewport;
};

// 使用 IntersectionObserver 监听触发元素是否离开视口
let cleanupIntersectionObserver: (() => void) | null = null;

const setupIntersectionObserver = () => {
  if (!triggerRef.value) return;

  cleanupIntersectionObserver = elementObserverManager.observeIntersection(
    triggerRef.value,
    isIntersecting => {
      if (!isIntersecting && visible.value) {
        hide();
      }
    },
  );
};

watch(visible, newVal => {
  if (newVal) {
    updatePosition();
    // 在显示时也检查一次是否在视口内
    nextTick(() => {
      checkTriggerInViewport();
    });
  }
});

// 监听 content 内容变化，重新计算位置
watch(
  () => props.content,
  () => {
    if (visible.value) {
      updatePosition();
    }
  },
);

onMounted(() => {
  const win = getWindow();
  if (win) {
    win.addEventListener('scroll', handleScroll, true);
    win.addEventListener('resize', handleScroll);
  }
  setupIntersectionObserver();
});

onUnmounted(() => {
  const win = getWindow();
  if (win) {
    win.removeEventListener('scroll', handleScroll, true);
    win.removeEventListener('resize', handleScroll);
  }
  if (cleanupIntersectionObserver) {
    cleanupIntersectionObserver();
    cleanupIntersectionObserver = null;
  }
  // 清理定时器
  if (hideTimer) {
    clearTimeout(hideTimer);
    hideTimer = null;
  }
});

defineExpose({
  visible,
  hide,
  show,
  handleScroll,
});
</script>

<style scoped lang="scss">
.tooltip-trigger {
  display: inline-block;
  width: fit-content;
}

.tooltip {
  position: absolute;
  background: var(--x-black-1);
  color: var(--x-white-1);
  border-radius: var(--x-radius-medium);
  font-size: 13px;
  line-height: 21px;

  word-wrap: break-word;
  word-break: break-word;
  z-index: 9999;
  transition: opacity 0.2s ease;
}

/* 深色主题(默认) */
.tooltip--dark {
  background: var(--x-black-1);
  color: var(--x-white-1);
}

/* 浅色主题 */
.tooltip--light {
  background: var(--x-white-1);
  color: var(--x-text-1);
}

.tooltip__content {
  padding: 8px 12px;
  line-height: 21px;
  // 允许在给定 max-width 下自动换行
  white-space: normal;
}

.tooltip__arrow {
  position: absolute;
  width: 10px;
  height: 4px;
}

.tooltip__arrow--dark {
  color: var(--x-black-1);
}

.tooltip__arrow--light {
  color: var(--x-white-1);
}

.tooltip__arrow--top {
  left: 50%;
  transform: translateX(-50%);
}

.tooltip__arrow--topLeft {
  bottom: -4px;
  left: 12px;
}

.tooltip__arrow--topRight {
  bottom: -4px;
  right: 12px;
}

.tooltip__arrow--bottom {
  left: 50%;
  top: -4px;
  transform: translateX(-50%) rotate(180deg);
}

.tooltip__arrow--bottomLeft {
  top: -4px;
  left: 12px;
  transform: rotate(180deg);
}

.tooltip__arrow--bottomRight {
  top: -4px;
  right: 12px;
  transform: rotate(180deg);
}

.tooltip__arrow--left {
  top: 50%;
  right: -7px;
  transform: translateY(-50%) rotate(-90deg);
}

.tooltip__arrow--leftTop {
  top: 12px;
  right: -7px;
  transform: rotate(-90deg);
}

.tooltip__arrow--leftBottom {
  bottom: 12px;
  right: -7px;
  transform: rotate(-90deg);
}

.tooltip__arrow--right {
  top: 50%;
  left: -7px;
  transform: translateY(-50%) rotate(90deg);
}

.tooltip__arrow--rightTop {
  top: 12px;
  left: -7px;
  transform: rotate(90deg);
}

.tooltip__arrow--rightBottom {
  bottom: 12px;
  left: -7px;
  transform: rotate(90deg);
}
</style>
